import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import NotificationCardMolecule from '../NotificationCard/NotificationCardMolecule';
import type { NotificationRecord } from '../NotificationCard/NotificationCardMolecule';
import CardStackMolecule from '../CardStack/CardStackMolecule';
import type { ViewMode } from '../CardStack/CardStackMolecule';
import './FeedNotificationStackMolecule.scss';

function getStorageKey(sphereId: string): string {
  return `feed:${sphereId}:notificationStackCollapsed`;
}

function getOldStorageKey(sphereId: string): string {
  return `feed:${sphereId}:alertStackCollapsed`;
}

/** Migrate old alertStackCollapsed key to notificationStackCollapsed. Idempotent. */
function migrateStorageKey(sphereId: string): void {
  try {
    const oldKey = getOldStorageKey(sphereId);
    const oldValue = localStorage.getItem(oldKey);
    if (oldValue !== null) {
      const newKey = getStorageKey(sphereId);
      if (localStorage.getItem(newKey) === null) {
        localStorage.setItem(newKey, oldValue);
      }
      localStorage.removeItem(oldKey);
    }
  } catch {
    // Silently ignore storage errors
  }
}

function loadCollapsed(sphereId: string): boolean {
  try {
    migrateStorageKey(sphereId);
    return localStorage.getItem(getStorageKey(sphereId)) === 'true';
  } catch {
    return false;
  }
}

function saveCollapsed(sphereId: string, collapsed: boolean): void {
  try {
    localStorage.setItem(getStorageKey(sphereId), String(collapsed));
  } catch {
    // Quota or access error — silently ignore
  }
}

function filterUrgentUnread(notifications: NotificationRecord[]): NotificationRecord[] {
  return notifications.filter(
    n => n.priority === 'urgent' && n.status !== 'read'
  );
}

function sortByMostRecent(notifications: NotificationRecord[]): NotificationRecord[] {
  return [...notifications].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

export interface FeedNotificationStackMoleculeProps {
  /** All notifications from context (filtering is done internally) */
  notifications: NotificationRecord[];
  /** Active sphere ID (used for localStorage key) */
  sphereId: string;
  /** Callback when a notification card is clicked */
  onNotificationClick?: (notification: NotificationRecord) => void;
  /** Callback when mark-as-read is triggered on a notification */
  onMarkRead?: (notificationId: string) => void;
}

const FeedNotificationStackMolecule: React.FC<FeedNotificationStackMoleculeProps> = ({
  notifications,
  sphereId,
  onNotificationClick,
  onMarkRead,
}) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(() => loadCollapsed(sphereId));
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | 'auto'>('auto');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('stack');

  const filteredNotifications = useMemo(
    () => sortByMostRecent(filterUrgentUnread(notifications)),
    [notifications]
  );

  // Clamp currentIndex when filteredNotifications changes
  useEffect(() => {
    if (filteredNotifications.length > 0 && currentIndex >= filteredNotifications.length) {
      setCurrentIndex(filteredNotifications.length - 1);
    }
  }, [filteredNotifications.length, currentIndex]);

  useEffect(() => {
    if (contentRef.current) {
      if (collapsed) {
        setContentHeight(contentRef.current.scrollHeight);
        requestAnimationFrame(() => setContentHeight(0));
      } else {
        setContentHeight(contentRef.current.scrollHeight);
        const timer = setTimeout(() => setContentHeight('auto'), 300);
        return () => clearTimeout(timer);
      }
    }
  }, [collapsed, filteredNotifications.length]);

  const handleToggle = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      saveCollapsed(sphereId, next);
      return next;
    });
  }, [sphereId]);

  const handleNavigateNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, filteredNotifications.length - 1));
  }, [filteredNotifications.length]);

  const handleNavigatePrev = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const handleToggleViewMode = useCallback(() => {
    setViewMode(prev => (prev === 'stack' ? 'fan' : 'stack'));
  }, []);

  if (filteredNotifications.length === 0) return null;

  return (
    <div className={`feed-notification-stack-molecule${collapsed ? ' feed-notification-stack-molecule--collapsed' : ''}`}>
      <button
        className="feed-notification-stack-molecule__header"
        onClick={handleToggle}
        type="button"
        aria-expanded={!collapsed}
      >
        <span className="feed-notification-stack-molecule__header-title">
          {t('feed.notificationStack.header', { count: filteredNotifications.length })}
        </span>
        <i className={`pi ${collapsed ? 'pi-chevron-down' : 'pi-chevron-up'}`} />
      </button>

      <div
        ref={contentRef}
        className="feed-notification-stack-molecule__content"
        style={{ height: typeof contentHeight === 'number' ? `${contentHeight}px` : contentHeight }}
        aria-hidden={collapsed}
      >
        <div className="feed-notification-stack-molecule__content-inner">
          <CardStackMolecule
            currentIndex={currentIndex}
            totalCount={filteredNotifications.length}
            viewMode={viewMode}
            onNavigateNext={handleNavigateNext}
            onNavigatePrev={handleNavigatePrev}
            onToggleViewMode={handleToggleViewMode}
            compact={true}
            navigationLayout="vertical"
          >
            {filteredNotifications.map(notification => (
              <NotificationCardMolecule
                key={notification.id}
                notification={notification}
                onClick={onNotificationClick}
                onMarkRead={onMarkRead}
              />
            ))}
          </CardStackMolecule>
        </div>
      </div>
    </div>
  );
};

export default FeedNotificationStackMolecule;
